import type { MountedExperiment, SliderRowHandle } from '@learnforge/demo-kit';
import { createSliderRow } from '@learnforge/demo-kit';
import { computeNaturalDomain, parse, sampleCurve, type ExprNode, type Interval } from '../func';
import { createActionBar } from '../action-bar';
import { createFunctionGraph, type FunctionGraphHandle } from '../render/function-graph';
import {
  createCartesianViewport,
  createFormulaLegend,
  createSvgEl,
  type CartesianViewportView,
} from '@learnforge/demo-render';
import type { FuncContext } from '../types';

export function rangeBoundsForDomain(
  node: ExprNode,
  domain: [number, number],
  samples: number = 600,
): [number, number] {
  const buf = sampleCurve(node, domain[0], domain[1], samples);
  let min = Infinity;
  let max = -Infinity;
  for (const y of buf) {
    if (Number.isFinite(y)) {
      if (y < min) min = y;
      if (y > max) max = y;
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [Number.NaN, Number.NaN];
  return [min, max];
}

export function naturalIntervalsWithin(node: ExprNode, bounds: [number, number]): Interval[] {
  const natural = computeNaturalDomain(node);
  const clipped = natural.intervals.flatMap((interval) => {
    const start = Math.max(bounds[0], interval.start);
    const end = Math.min(bounds[1], interval.end);
    if (start > end) return [];
    return [
      {
        start,
        end,
        openLeft: start === interval.start ? interval.openLeft : false,
        openRight: end === interval.end ? interval.openRight : false,
      },
    ];
  });

  for (const exclusion of [...natural.excluded].sort((a, b) => a.x - b.x)) {
    const containingIndex = clipped.findIndex(
      (interval) => exclusion.x >= interval.start && exclusion.x <= interval.end,
    );
    if (containingIndex < 0) continue;
    const containing = clipped[containingIndex]!;
    if (exclusion.x === containing.start) {
      clipped[containingIndex] = { ...containing, openLeft: true };
      continue;
    }
    if (exclusion.x === containing.end) {
      clipped[containingIndex] = { ...containing, openRight: true };
      continue;
    }
    clipped.splice(
      containingIndex,
      1,
      { ...containing, end: exclusion.x, openRight: true },
      { ...containing, start: exclusion.x, openLeft: true },
    );
  }
  return clipped;
}

function formatBound(value: number): string {
  if (!Number.isFinite(value)) return value < 0 ? '−∞' : '∞';
  return String(Number(value.toFixed(2))).replace('-', '−');
}

function formatNaturalDomain(node: ExprNode): string {
  const natural = computeNaturalDomain(node);
  if (
    natural.intervals.length === 1 &&
    natural.intervals[0]?.start === -Infinity &&
    natural.intervals[0]?.end === Infinity &&
    natural.excluded.length > 0
  ) {
    return natural.excluded.map((point) => `x ≠ ${formatBound(point.x)}`).join(', ');
  }
  const intervals = naturalIntervalsWithin(node, [-Infinity, Infinity]);
  if (intervals.length === 0) return '∅';
  return intervals
    .map(
      (interval) =>
        `${interval.openLeft ? '(' : '['}${formatBound(interval.start)}, ${formatBound(interval.end)}${interval.openRight ? ')' : ']'}`,
    )
    .join(' ∪ ');
}

export function mountDomainRangeLab(ctx: FuncContext): MountedExperiment {
  let fSrc = '1/(x - 2) + 3';
  let domain: [number, number] = [-10, 10];
  let range: [number, number] = [-10, 10];
  const enabled: Record<'domain' | 'range', [boolean, boolean]> = {
    domain: [false, false],
    range: [false, false],
  };
  let naturalDomainOn = false;

  const svg = createSvgEl('svg', { viewBox: '0 0 800 460', class: 'fn-stage' });
  ctx.root.appendChild(svg);
  const stageG = createSvgEl('g');
  svg.appendChild(stageG);
  const legend = createFormulaLegend(ctx.root);
  let view: CartesianViewportView = { xDomain: [-10, 10], yDomain: [-10, 10], zoom: 1 };

  const readouts = document.createElement('div');
  readouts.className = 'fn-domain-range-readouts';
  const domainEl = document.createElement('div');
  domainEl.className = 'fn-domain-readout';
  const rangeEl = document.createElement('div');
  rangeEl.className = 'fn-range-readout';
  readouts.append(domainEl, rangeEl);
  ctx.root.appendChild(readouts);

  let graphHandle: FunctionGraphHandle | null = null;
  function redraw(): void {
    const node = parse(fSrc).node;
    const sampleCount = Math.max(320, Math.round(800 * (globalThis.devicePixelRatio || 1)));
    const sample = sampleCurve(node, view.xDomain[0], view.xDomain[1], sampleCount);
    const naturalDomain = computeNaturalDomain(node);
    const domainBounds: [number, number] = [
      enabled.domain[0] ? domain[0] : -Infinity,
      enabled.domain[1] ? domain[1] : Infinity,
    ];
    const rangeBounds: [number, number] = [
      enabled.range[0] ? range[0] : -Infinity,
      enabled.range[1] ? range[1] : Infinity,
    ];
    const visibleDomain: [number, number] = [
      Math.max(view.xDomain[0], domainBounds[0]),
      Math.min(view.xDomain[1], domainBounds[1]),
    ];
    const visibleRange: [number, number] = [
      Math.max(view.yDomain[0], rangeBounds[0]),
      Math.min(view.yDomain[1], rangeBounds[1]),
    ];
    const selectedDomain: Interval = {
      start: visibleDomain[0],
      end: visibleDomain[1],
      openLeft: false,
      openRight: false,
    };
    const shaded = naturalDomainOn
      ? naturalIntervalsWithin(node, visibleDomain)
      : enabled.domain.some(Boolean) && visibleDomain[0] <= visibleDomain[1]
        ? [selectedDomain]
        : [];
    const sampledRange =
      visibleDomain[0] <= visibleDomain[1]
        ? rangeBoundsForDomain(node, visibleDomain)
        : [Number.NaN, Number.NaN];
    const constraintsOn = enabled.domain.some(Boolean) || enabled.range.some(Boolean);

    const options = {
      xDomain: view.xDomain,
      yDomain: view.yDomain,
      width: 800,
      height: 460,
      curves: [
        ...(constraintsOn ? [{ sample, className: 'fn-curve-context' }] : []),
        {
          sample,
          className: 'fn-curve-primary',
          label: 'f(x)',
          clipBounds: { xDomain: domainBounds, yDomain: rangeBounds },
        },
      ],
      asymptotes: naturalDomain.excluded.map((e) => ({ kind: 'vertical' as const, value: e.x })),
      shadedDomain: shaded,
      shadedRange:
        enabled.range.some(Boolean) && visibleRange[0] <= visibleRange[1]
          ? [{ start: visibleRange[0], end: visibleRange[1], openLeft: false, openRight: false }]
          : [],
      i18n: ctx.i18n,
    };
    if (graphHandle) graphHandle.update(options);
    else graphHandle = createFunctionGraph(stageG, options);
    legend.setEntries([{ id: 'function', formula: `f(x) = ${fSrc}`, color: 'var(--accent)' }]);
    domainEl.textContent = naturalDomainOn
      ? `${ctx.i18n.t('readout.natural-domain')}: ${formatNaturalDomain(node)}`
      : `${ctx.i18n.t('readout.selected-domain')}: ${enabled.domain[0] ? '[' : '('}${formatBound(domainBounds[0])}, ${formatBound(domainBounds[1])}${enabled.domain[1] ? ']' : ')'}`;
    rangeEl.textContent =
      Number.isFinite(sampledRange[0]) && Number.isFinite(sampledRange[1])
        ? `${ctx.i18n.t('readout.image-of-domain')} ≈ [${sampledRange[0]!.toFixed(2)}, ${sampledRange[1]!.toFixed(2)}]`
        : `${ctx.i18n.t('readout.image-of-domain')}: ${ctx.i18n.t('readout.no-finite-outputs')}`;
  }

  function addInput(label: string, initial: string, onChange: (v: string) => void): void {
    const row = document.createElement('div');
    row.className = 'fn-panel-row';
    const l = document.createElement('label');
    l.textContent = label;
    const input = document.createElement('input');
    input.type = 'text';
    input.value = initial;
    input.addEventListener('change', () => {
      try {
        parse(input.value);
        onChange(input.value);
        input.classList.remove('fn-input-error');
      } catch {
        input.classList.add('fn-input-error');
      }
    });
    row.appendChild(l);
    row.appendChild(input);
    ctx.panel.appendChild(row);
  }

  addInput(ctx.i18n.t('param.f.label'), fSrc, (v) => {
    fSrc = v;
    naturalDomainOn = false;
    redraw();
  });

  const sliderDefs: {
    id: string;
    labelKey: string;
    target: 'domain' | 'range';
    idx: 0 | 1;
  }[] = [
    { id: 'domain-start', labelKey: 'param.domain-start.label', target: 'domain', idx: 0 },
    { id: 'domain-end', labelKey: 'param.domain-end.label', target: 'domain', idx: 1 },
    {
      id: 'range-start',
      labelKey: 'param.output-range-start.label',
      target: 'range',
      idx: 0,
    },
    { id: 'range-end', labelKey: 'param.output-range-end.label', target: 'range', idx: 1 },
  ];
  const sliderHandles: Record<string, SliderRowHandle> = {};
  const boundToggles: Record<string, HTMLInputElement> = {};
  function syncControls(): void {
    for (const def of sliderDefs) {
      const handle = sliderHandles[def.id]!;
      const isEnabled = enabled[def.target][def.idx];
      boundToggles[def.id]!.checked = isEnabled;
      handle.input.disabled = !isEnabled;
      handle.valueInput.disabled = !isEnabled;
      handle.setValue((def.target === 'domain' ? domain : range)[def.idx]);
    }
  }

  function setBound(target: 'domain' | 'range', index: 0 | 1, value: number): void {
    const values = target === 'domain' ? domain : range;
    const opposite = index === 0 ? 1 : 0;
    values[index] = enabled[target][opposite]
      ? index === 0
        ? Math.min(value, values[1])
        : Math.max(value, values[0])
      : value;
  }

  for (const def of sliderDefs) {
    const values = def.target === 'domain' ? domain : range;
    const handle = createSliderRow({
      id: def.id,
      labelKey: def.labelKey,
      i18n: ctx.i18n,
      min: -10,
      max: 10,
      step: 0.1,
      value: values[def.idx],
      format: (v) => v.toFixed(2),
      onChange: (v) => {
        if (!enabled[def.target][def.idx]) return;
        setBound(def.target, def.idx, v);
        syncControls();
        redraw();
      },
    });
    sliderHandles[def.id] = handle;
    handle.element.classList.add('fn-bound-row');
    const toggle = document.createElement('input');
    toggle.type = 'checkbox';
    toggle.id = `enable-${def.id}`;
    toggle.dataset.boundToggle = def.id;
    handle.valueInput.id = `value-${def.id}`;
    toggle.setAttribute('aria-controls', `${handle.input.id} ${handle.valueInput.id}`);
    const label = handle.element.querySelector<HTMLLabelElement>('.dim-label')!;
    label.id = `label-${def.id}`;
    label.htmlFor = toggle.id;
    const labelRow = document.createElement('div');
    labelRow.className = 'fn-bound-label';
    handle.element.prepend(labelRow);
    labelRow.append(toggle, label);
    handle.input.setAttribute('aria-labelledby', label.id);
    boundToggles[def.id] = toggle;
    toggle.addEventListener('change', () => {
      enabled[def.target][def.idx] = toggle.checked;
      if (toggle.checked)
        setBound(def.target, def.idx, (def.target === 'domain' ? domain : range)[def.idx]);
      syncControls();
      redraw();
    });
    ctx.panel.appendChild(handle.element);
  }
  syncControls();

  redraw();
  const viewport = createCartesianViewport(svg, {
    base: { xDomain: [-10, 10], yDomain: [-10, 10] },
    plotRect: { x: 0, y: 0, width: 800, height: 460 },
    onChange(next) {
      view = next;
      redraw();
    },
  });

  createActionBar(
    ctx.actionBar,
    [
      {
        id: 'auto',
        labelKey: ctx.i18n.t('btn.auto-domain'),
        onClick: () => {
          const natural = naturalIntervalsWithin(parse(fSrc).node, [-Infinity, Infinity]);
          const first = natural[0];
          const last = natural.at(-1);
          naturalDomainOn = true;
          enabled.domain = [
            first !== undefined && first.start >= -10 && first.start <= 10,
            last !== undefined && last.end >= -10 && last.end <= 10,
          ];
          domain = [enabled.domain[0] ? first!.start : -10, enabled.domain[1] ? last!.end : 10];
          syncControls();
          redraw();
        },
      },
      {
        id: 'reset',
        labelKey: ctx.i18n.t('btn.reset'),
        onClick: () => {
          domain = [-10, 10];
          range = [-10, 10];
          enabled.domain = [false, false];
          enabled.range = [false, false];
          naturalDomainOn = false;
          syncControls();
          redraw();
        },
      },
    ],
    { viewReset: { labelKey: ctx.i18n.t('btn.reset-view'), onResetView: () => viewport.reset() } },
  );
  return {
    dispose() {
      graphHandle?.destroy();
      viewport.dispose();
      legend.destroy();
      svg.remove();
      readouts.remove();
      ctx.panel.replaceChildren();
      ctx.actionBar.replaceChildren();
    },
  };
}
